export default function StrategicPlanningPage() {
  return (
    <div className="min-h-screen bg-white font-playfair">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center justify-center w-full md:w-auto">
              <h1>
                <a href="/">
                  <img 
                    src="https://static.readdy.ai/image/a19705f045df358eda503236ce656b36/846ee1e93f81f94b53dc3e9fa39ac791.png" 
                    alt="VTC Legal - Fractional General Counsel for AI and Tech Companies" 
                    className="h-16 w-auto"
                  />
                </a>
              </h1>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-10">
              <a href="/#services" className="text-gray-900 hover:text-teal-600 font-medium tracking-wide transition-colors">Services</a>
              <a href="/#why-vtc" className="text-gray-900 hover:text-teal-600 font-medium tracking-wide transition-colors">Why VTC</a>
              <a href="/#how-it-works" className="text-gray-900 hover:text-teal-600 font-medium tracking-wide transition-colors">How It Works</a>
              <a href="/#contact" className="bg-black text-white px-8 py-3 rounded-md hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer font-medium">Get Started</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-bold text-gray-900 mb-8 font-playfair">
            Strategic Planning
          </h2>
          <p className="text-xl text-gray-700 mb-12 max-w-3xl mx-auto leading-relaxed font-inter">
            Legal strategy aligned with business objectives. We help technology leaders make informed decisions that drive growth while managing risk.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-8 font-playfair">Legal as Business Strategy</h3>
              <div className="space-y-6">
                <p className="text-gray-700 leading-relaxed font-inter">
                  The best legal counsel doesn't just react to problems—it anticipates opportunities and risks, helping shape business strategy from a position of strength.
                </p>
                <p className="text-gray-700 leading-relaxed font-inter">
                  Our strategic planning approach integrates legal considerations into your business planning process, ensuring legal strategy supports rather than constrains growth.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-8 font-playfair">Proactive Partnership</h3>
              <div className="space-y-6">
                <p className="text-gray-700 leading-relaxed font-inter">
                  Having served as Legal Director at technology companies, I understand the balance between legal precision and business pragmatism required for startup success.
                </p>
                <p className="text-gray-700 leading-relaxed font-inter">
                  We work as an extension of your leadership team, providing strategic counsel that considers both immediate needs and long-term objectives.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Areas */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-4xl font-bold text-gray-900 mb-16 text-center font-playfair">Strategic Legal Planning Areas</h3>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div className="bg-gray-50 p-8 rounded-lg">
                <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-roadmap-line text-white text-xl"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Growth Strategy</h4>
                <p className="text-gray-700 font-inter mb-4">
                  Legal infrastructure planning that scales with business growth, from startup through IPO or acquisition.
                </p>
                <ul className="text-gray-600 space-y-2 text-sm font-inter">
                  <li>• Market expansion legal requirements</li>
                  <li>• International growth planning</li>
                  <li>• Regulatory pathway mapping</li>
                  <li>• Partnership strategy development</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-shield-keyhole-line text-white text-xl"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Risk Management</h4>
                <p className="text-gray-700 font-inter mb-4">
                  Proactive identification and mitigation of legal risks before they impact business operations or valuation.
                </p>
                <ul className="text-gray-600 space-y-2 text-sm font-inter">
                  <li>• Enterprise risk assessment</li>
                  <li>• Compliance framework design</li>
                  <li>• Crisis management planning</li>
                  <li>• Insurance strategy optimization</li>
                </ul>
              </div>
            </div>

            <div className="space-y-8">
              <div className="bg-gray-50 p-8 rounded-lg">
                <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-funds-box-line text-white text-xl"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Capital Strategy</h4>
                <p className="text-gray-700 font-inter mb-4">
                  Strategic planning for fundraising, debt financing, and capital allocation with legal optimization.
                </p>
                <ul className="text-gray-600 space-y-2 text-sm font-inter">
                  <li>• Fundraising timeline planning</li>
                  <li>• Investor relations strategy</li>
                  <li>• Debt structure optimization</li>
                  <li>• Exit strategy preparation</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-lightbulb-flash-line text-white text-xl"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Innovation Protection</h4>
                <p className="text-gray-700 font-inter mb-4">
                  Intellectual property strategy that protects innovations while enabling collaborative development and partnerships.
                </p>
                <ul className="text-gray-600 space-y-2 text-sm font-inter">
                  <li>• IP portfolio development</li>
                  <li>• Trade secret strategies</li>
                  <li>• Open source compliance</li>
                  <li>• Technology licensing models</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Process */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-4xl font-bold text-gray-900 mb-12 text-center font-playfair">Strategic Planning Process</h3>
          
          <div className="space-y-12">
            <div className="flex items-start">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mr-8 flex-shrink-0">
                <span className="text-white text-2xl font-bold font-inter">1</span>
              </div>
              <div className="pt-4">
                <h4 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Business Context Analysis</h4>
                <p className="text-gray-700 leading-relaxed font-inter mb-6">
                  Deep dive into your business model, competitive landscape, growth objectives, and risk tolerance to understand the strategic context for legal planning.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <ul className="text-gray-600 space-y-2 text-sm font-inter">
                    <li>• Market position assessment</li>
                    <li>• Competitive advantage analysis</li>
                    <li>• Growth trajectory mapping</li>
                  </ul>
                  <ul className="text-gray-600 space-y-2 text-sm font-inter">
                    <li>• Stakeholder priority alignment</li>
                    <li>• Resource allocation review</li>
                    <li>• Strategic timeline development</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mr-8 flex-shrink-0">
                <span className="text-white text-2xl font-bold font-inter">2</span>
              </div>
              <div className="pt-4">
                <h4 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Legal Infrastructure Audit</h4>
                <p className="text-gray-700 leading-relaxed font-inter mb-6">
                  Comprehensive review of existing legal structures, contracts, compliance status, and risk exposures to identify optimization opportunities.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <ul className="text-gray-600 space-y-2 text-sm font-inter">
                    <li>• Corporate structure review</li>
                    <li>• Contract portfolio analysis</li>
                    <li>• Compliance gap assessment</li>
                  </ul>
                  <ul className="text-gray-600 space-y-2 text-sm font-inter">
                    <li>• IP asset evaluation</li>
                    <li>• Risk exposure mapping</li>
                    <li>• Cost-benefit analysis</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mr-8 flex-shrink-0">
                <span className="text-white text-2xl font-bold font-inter">3</span>
              </div>
              <div className="pt-4">
                <h4 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Strategic Roadmap Development</h4>
                <p className="text-gray-700 leading-relaxed font-inter mb-6">
                  Create prioritized action plans that align legal initiatives with business objectives, including timeline, resource requirements, and success metrics.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <ul className="text-gray-600 space-y-2 text-sm font-inter">
                    <li>• Priority initiative identification</li>
                    <li>• Resource requirement planning</li>
                    <li>• Timeline optimization</li>
                  </ul>
                  <ul className="text-gray-600 space-y-2 text-sm font-inter">
                    <li>• Success metrics definition</li>
                    <li>• Risk mitigation strategies</li>
                    <li>• Implementation monitoring</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Outcomes */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-4xl font-bold text-gray-900 mb-16 text-center font-playfair">Strategic Outcomes We Deliver</h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-gray-900 rounded-2xl flex items-center justify-center mx-auto mb-8">
                <i className="ri-speed-line text-white text-3xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Accelerated Growth</h4>
              <p className="text-gray-700 font-inter">
                Legal strategies that remove barriers to growth, enabling faster market expansion and revenue scaling.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gray-900 rounded-2xl flex items-center justify-center mx-auto mb-8">
                <i className="ri-shield-star-line text-white text-3xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Risk Optimization</h4>
              <p className="text-gray-700 font-inter">
                Balanced risk management that protects the business without constraining innovation or competitive advantage.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gray-900 rounded-2xl flex items-center justify-center mx-auto mb-8">
                <i className="ri-money-dollar-circle-line text-white text-3xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Value Creation</h4>
              <p className="text-gray-700 font-inter">
                Legal structures and strategies that enhance enterprise value and improve investment attractiveness.
              </p>
            </div>
          </div>

          <div className="mt-16 bg-gray-50 p-12 rounded-2xl">
            <h4 className="text-2xl font-bold text-gray-900 mb-8 text-center font-playfair">Monthly Strategic Sessions</h4>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h5 className="font-semibold text-gray-900 mb-4 font-inter">Business Strategy Alignment</h5>
                <ul className="text-gray-700 space-y-2 text-sm font-inter">
                  <li>• Quarterly business plan review</li>
                  <li>• Legal priority adjustment</li>
                  <li>• Resource allocation optimization</li>
                  <li>• Market opportunity assessment</li>
                </ul>
              </div>
              <div>
                <h5 className="font-semibold text-gray-900 mb-4 font-inter">Proactive Risk Management</h5>
                <ul className="text-gray-700 space-y-2 text-sm font-inter">
                  <li>• Emerging risk identification</li>
                  <li>• Regulatory change monitoring</li>
                  <li>• Compliance status updates</li>
                  <li>• Strategic course corrections</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-4xl font-bold text-white mb-8 font-playfair">
            Ready to Align Legal with Business Strategy?
          </h3>
          <p className="text-xl text-gray-300 mb-12 font-inter">
            Get strategic legal counsel that drives growth while protecting your business interests.
          </p>
          <a href="/#contact" className="bg-teal-600 text-white px-12 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer inline-block font-inter">
            Schedule Consultation
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 text-gray-900 py-20 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-600 text-sm font-inter">
            © 2026 VTC Legal. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}